import React from "react";

import ResultModal from "@/components/modal/result-modal/result-modal";
import { ModalType } from "@/providers/modal-provider/modal-context.types";
import assertUnreachable from "@/utils/assert-unreachable";
import SidebarModal from "@/components/modal/sidebar-modal/sidebar-modal";
import ErrorModal from "@/components/modal/error-modal/error-modal";
import MessageModal from "@/components/modal/message-modal/message-modal";
import HowToTrainModal from "@/components/pages/custom/modals/how-to-train-modal";
import EditCustomModal from "@/components/pages/custom/modals/edit-custom-modal";
import TrialErrorModal from "@/components/modal/trial-error-modal/trial-error-modal";
import CustomVoicesLimitedModal from "@/components/modal/license-limited-modals/custom-voices-limited-modal";
import FileLimitedModal from "@/components/modal/license-limited-modals/file-limited-modal";
import RequestVoiceModal from "@/components/modal/request-voice-modal/request-voice-modal";
import CustomDatasetLimitModal from "@/components/pages/custom/modals/custom-dataset-limit-modal";
import ShareSocialMediaModal from "@/components/modal/share-social-media-modal/share-social-media-modal";
import StemResultModal from "@/components/modal/result-modal/stem-result-modal";
import SoonFeatureModal from "@/components/modal/soon-feature-modal/soon-feature-modal";
import ConfirmDeleteModal from "@/components/modal/confirm-modal/confirm-delete-modal";
import HalloweenModal from "@/components/modal/halloween-modal/halloween-modal";
import YoutubeEmbedModal from "@/components/modal/youtube-embed-modal/youtube-embed-modal";
import SuccessCreatedCustomModal from "@/components/pages/custom/modals/success-created-custom-modal";
import LicenseLimitedModal from "@/components/modal/license-limited-modals/license-limited-modal";
import StemSplitterCountLimitedModal from "@/components/modal/license-limited-modals/stem-splitter-count-limited-modal";
import ConvertsCountLimitedModal from "@/components/modal/license-limited-modals/converts-count-limited-modal";
import VocalRemoverCountLimitedModal from "@/components/modal/license-limited-modals/vocal-remover-count-limited-modal";
import TextToMusicCountLimitedModal from "@/components/modal/license-limited-modals/text-to-music-count-limited-modal";
import TextToMusicLimitedDurationModal from "@/components/modal/license-limited-modals/text-to-music-limited-duration-modal";
import LikeConvertModal from "@/components/modal/rate-convert-modals/like-convert-modal";
import DislikeConvertModal from "@/components/modal/rate-convert-modals/dislike-convert-modal";
import DefaultVoiceSelectorModal from "@/components/modal/voice-selector/default-voice-selector-modal";
import BlendingVoiceSelectorModal from "@/components/modal/voice-selector/blending-voice-selector-modal";
import CompleteModal from "@/components/modal/complete-model/complete-modal";
import TextToSpeechCountLimitedModal from "@/components/modal/license-limited-modals/text-to-speech-count-limited-modal";
import TtsVoiceSelectorModal from "@/components/modal/voice-selector/tts-voice-selector-modal";
import SubscriptionExpiredModal from "@/components/modal/license-limited-modals/subscription-expired-modal";
import ReferFriendModal from "@/components/modal/refer-friend/refer-friend-modal";
import ConvertLimitExpiredCreditModal from "@/components/modal/license-limited-modals/credit/convert-limit-expired-credit-modal";
import CustomVoiceLimitExpiredModal from "@/components/modal/license-limited-modals/credit/custom-voice-limit-expired-modal";
import AffiliateModal from "@/components/modal/affiliate/affiliate-modal";
import DuetsVoiceSelectorModal from "@/components/modal/voice-selector/duets-voice-selector-modal";
import MediaFormatsInputModal from "@/components/modal/media-formats-input-modal/media-formats-input-modal";
import DuetsResultModal from "@/components/modal/duets-result-modal/duets-result-modal";
import DuetsCountLimitedModal from "@/components/modal/license-limited-modals/duets-count-limited-modal";
import CreateNewTrainModal from "@/components/pages/custom/modals/create-new-train-modal";
import NeedSignInModal from "@/components/modal/anon-services-modals/need-sign-in-modal";
import ShareVoiceModal from "@/components/modal/share-voice/share-voice-modal";
import ResultLimitedModal from "@/components/modal/license-limited-modals/result-limited-modal";
import InvalidFileModal from "@/components/modal/invalid-file-modal/invalid-file-modal";
import DuetConfigModal from "@/components/modal/duet-config-modal/duet-config-modal";
import CorruptModelModal from "@/components/modal/corrupt-model-modal/corrupt-model-modal";
import OnboardingInputModal from "@/components/modal/onboarding-input-modal/onboarding-input-modal";
import PaywallModal from "@/components/modal/paywall-modal/paywall-modal";
import UndoCancelModal from "@/components/modal/undo-cancel/undo-cancel-modal";

const renderModalByType = (modalType: ModalType): React.JSX.Element => {
  switch (modalType.type) {
    case "all_models":
      return <DefaultVoiceSelectorModal />;
    case "tts_voice_selector_modal":
      return <TtsVoiceSelectorModal />;
    case "blending_voice_selector_modal":
      const { otherVoice, handleVoiceModelClick } = modalType.props;
      return (
        <BlendingVoiceSelectorModal
          otherVoice={otherVoice}
          handleVoiceModelClick={handleVoiceModelClick}
        />
      );
    case "duets_voice_selector_modal":
      return <DuetsVoiceSelectorModal {...modalType.props} />;
    case "result_modal": {
      const {
        combinedFileName,
        vocalFileName,
        instrumentalFileName,
        icon,
        format,
        onSongsDownloaded,
        isOpen,
        convertId,
      } = modalType.props;
      return (
        <ResultModal
          combinedFileName={combinedFileName}
          instrumentalFileName={instrumentalFileName}
          vocalFileName={vocalFileName}
          format={format}
          icon={icon}
          onSongsDownloaded={onSongsDownloaded}
          isOpen={isOpen}
          convertId={convertId}
        />
      );
    }
    case "stem_result_modal":
      const {
        guitarFileName,
        pianoFileName,
        drumsFileName,
        bassFileName,
        vocalsFileName,
        othersFileName,
        icon,
        format,
        onSongsDownloaded,
        isOpen,
        splitId,
      } = modalType.props;
      return (
        <StemResultModal
          guitarFileName={guitarFileName}
          pianoFileName={pianoFileName}
          drumsFileName={drumsFileName}
          bassFileName={bassFileName}
          vocalsFileName={vocalsFileName}
          othersFileName={othersFileName}
          format={format}
          icon={icon}
          onSongsDownloaded={onSongsDownloaded}
          isOpen={isOpen}
          splitId={splitId}
        />
      );
    case "license_limited_modal":
      return <LicenseLimitedModal {...modalType.props} />;
    case "subscription_expired_modal":
      return (
        <SubscriptionExpiredModal
          licenseName={modalType.props?.licenseName}
          shouldNavigateToPricing={modalType.props?.shouldNavigateToPricing}
        />
      );
    case "custom-voices-limited_modal":
      return (
        <CustomVoicesLimitedModal licenseName={modalType.props.licenseName} />
      );
    case "sidebar_modal":
      return <SidebarModal />;
    case "error_modal":
      return (
        <ErrorModal
          title={modalType.props?.title}
          message={modalType.props?.message}
          feedbackData={modalType.props?.feedbackData}
        />
      );
    case "message_modal":
      return (
        <MessageModal
          title={modalType.props?.title}
          message={modalType.props?.message}
        />
      );
    case "complete_modal":
      const { message } = modalType.props;
      return <CompleteModal message={message} />;
    case "how_to_train":
      return <HowToTrainModal />;
    case "edit_custom":
      return (
        <EditCustomModal
          voiceId={modalType.props.voiceId}
          voiceName={modalType.props.voiceName}
        />
      );
    case "create_custom":
      return <CreateNewTrainModal />;
    case "trial_error":
      return <TrialErrorModal />;
    case "request_voice":
      return <RequestVoiceModal />;
    case "custom_dataset_limit_modal":
      return (
        <CustomDatasetLimitModal licenseName={modalType.props.licenseName} />
      );
    case "share_social_media_modal":
      return (
        <ShareSocialMediaModal
          url={modalType.props.url}
          sharedMessageTitle={modalType.props.sharedMessageTitle}
        />
      );
    case "confirm_delete_modal":
      const { deletionCallback } = modalType.props;
      return (
        <ConfirmDeleteModal
          deleteItemName={modalType.props.deleteItemName}
          deletionCallback={deletionCallback}
        />
      );
    case "soon_feature_modal":
      return <SoonFeatureModal featureName={modalType.props.featureName} />;
    case "success_created_custom_modal":
      return <SuccessCreatedCustomModal />;
    case "halloween_modal":
      return <HalloweenModal />;
    case "youtube_embed_modal":
      return (
        <YoutubeEmbedModal
          title={modalType.props.title}
          desktopYoutubeVideoId={modalType.props.desktopYoutubeVideoId}
          mobileYoutubeVideoId={modalType.props.mobileYoutubeVideoId}
        />
      );
    case "file_limited_modal":
      return (
        <FileLimitedModal
          licenseName={modalType.props.licenseName}
          limitationType={modalType.props.limitationType}
          limit={modalType.props.limit}
        />
      );
    case "stem-splitter-count-limited_modal":
      return (
        <StemSplitterCountLimitedModal
          licenseName={modalType.props.licenseName}
        />
      );
    case "converts-count-limited_modal":
      return (
        <ConvertsCountLimitedModal
          licenseName={modalType.props.licenseName}
          shouldNavigateToPricing={modalType.props?.shouldNavigateToPricing}
        />
      );
    case "vocal-remover-count-limited_modal":
      return (
        <VocalRemoverCountLimitedModal
          licenseName={modalType.props.licenseName}
        />
      );
    case "text-to-music-count-limited_modal":
      return (
        <TextToMusicCountLimitedModal
          licenseName={modalType.props.licenseName}
        />
      );
    case "text-to-speech-count-limited_modal":
      return (
        <TextToSpeechCountLimitedModal
          licenseName={modalType.props.licenseName}
        />
      );
    case "text-to-music-duration-limited_modal":
      return (
        <TextToMusicLimitedDurationModal
          licenseName={modalType.props.licenseName}
        />
      );
    case "like_convert":
      return <LikeConvertModal convertId={modalType.props.convertId} />;
    case "dislike_convert":
      return <DislikeConvertModal convertId={modalType.props.convertId} />;
    case "refer_friend":
      return <ReferFriendModal />;
    case "convert_limit_credit_modal":
      return (
        <ConvertLimitExpiredCreditModal
          handleUseCreditsClick={modalType.props.handleUseCreditsClick}
        />
      );
    case "custom_voice_limit_credit_modal":
      return (
        <CustomVoiceLimitExpiredModal
          handleUseCreditsClick={modalType.props.handleUseCreditsClick}
        />
      );
    case "affiliate_modal":
      return <AffiliateModal />;
    case "media-formats-input-modal":
      return <MediaFormatsInputModal {...modalType.props} />;
    case "duets_result_modal":
      return <DuetsResultModal {...modalType.props} />;
    case "duets_count_limited_modal":
      return <DuetsCountLimitedModal {...modalType.props} />;
    case "share-voice-modal":
      return <ShareVoiceModal {...modalType.props} />;
    case "need_sign_in_modal":
      return <NeedSignInModal serviceName={modalType.props.serviceName} />;
    case "result-limited-modal":
      return <ResultLimitedModal {...modalType.props} />;
    case "invalid-file-modal":
      return <InvalidFileModal />;
    case "duet-config-modal":
      return <DuetConfigModal {...modalType.props} />;
    case "corrupt-model-modal":
      return <CorruptModelModal />;
    case "onboarding-input-modal":
      return <OnboardingInputModal />;
    case "paywall-modal":
      return <PaywallModal />;
    case "undo-cancel-modal":
      return <UndoCancelModal />;
    default:
      assertUnreachable(modalType);
  }
};

export default renderModalByType;
